import { nearestSixteenth, Vertex2 } from '@rta/rta-blueprint-library';

import { BREAKPOINT_SPACING } from './constants';
import { Vector } from '@r-core/types';

/**
 * Checks if a vector crosses a vertical line at the given x position
 */
export const vectorCrossesX = (vector: Vector<Vertex2>, splitX: number): boolean => {
	const minX = nearestSixteenth(Math.min(vector.from.x, vector.to.x));
	const maxX = nearestSixteenth(Math.max(vector.from.x, vector.to.x));
	const roundedSplitX = nearestSixteenth(splitX);

	return minX < roundedSplitX && maxX > roundedSplitX;
};

/**
 * Calculates the y-coordinate where a vector crosses a vertical line at splitX
 */
const calculateYAtX = (vector: Vector<Vertex2>, splitX: number): number => {
	const { from, to } = vector;

	// Avoid division by zero for vertical vectors
	if (from.x === to.x) return from.y;

	const t = (splitX - from.x) / (to.x - from.x);
	return nearestSixteenth(from.y + t * (to.y - from.y));
};

/**
 * Splits a vector at the given x position, creating left and right portions
 * The right portion is shifted by BREAKPOINT_SPACING
 */
export const splitVectorAtX = (
	vector: Vector<Vertex2>,
	splitX: number
): { left: Vector<Vertex2>, right: Vector<Vertex2>, yAtSplit: number } => {
	const { from, to } = vector;
	const yAtSplit = calculateYAtX(vector, splitX);

	const goesLeftToRight = from.x < to.x;

	if (goesLeftToRight) {
		return {
			left: {
				...vector,
				from: new Vertex2({ dataIn: { x: from.x, y: from.y }}),
				to: new Vertex2({ dataIn: { x: splitX, y: yAtSplit }})
			},
			right: {
				...vector,
				from: new Vertex2({ dataIn: { x: splitX + BREAKPOINT_SPACING, y: yAtSplit }}),
				to: new Vertex2({ dataIn: { x: to.x + BREAKPOINT_SPACING, y: to.y }})
			},
			yAtSplit
		};
	}

	// Right to left direction
	return {
		right: {
			...vector,
			from: new Vertex2({ dataIn: { x: from.x + BREAKPOINT_SPACING, y: from.y }}),
			to: new Vertex2({ dataIn: { x: splitX + BREAKPOINT_SPACING, y: yAtSplit }})
		},
		left: {
			...vector,
			from: new Vertex2({ dataIn: { x: splitX, y: yAtSplit }}),
			to: new Vertex2({ dataIn: { x: to.x, y: to.y }})
		},
		yAtSplit
	};
};

